// WebSocket Manager for CTM Chat Widget
import { getTabReconnectAttempts, setTabReconnectAttempts } from './utils.js';

export const HEARTBEAT_REQUEST = '{"type":"ping"}';
export const HEARTBEAT_RESPONSE = '{"type":"pong"}';

export class WebSocketManager {
  constructor(config, logger) {
    this.config = config;
    this.logger = logger || console;
    this.webSocket = null;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.connectionStatus = 'disconnected';
    
    // Heartbeat properties
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.HEARTBEAT_INTERVAL_MS = 30000; // Send ping every 30 seconds
    this.HEARTBEAT_TIMEOUT_MS = 10000; // Wait 10 seconds for pong response
    this.lastPingTime = null;
    
    // Event listeners
    this.listeners = {
      open: [],
      message: [],
      close: [],
      error: [],
      statusChange: []
    };
  }

  // Event handling methods
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  // Connection methods
  connect(params) {
    if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
      this.logger.debug("WebSocket: Already open.");
      return Promise.resolve();
    }
    
    if (this.webSocket && this.webSocket.readyState === WebSocket.CONNECTING) {
      this.logger.debug("WebSocket: Already connecting.");
      // Return existing connection promise if available
      return this.connectionPromise || Promise.resolve();
    }

    // Initialize reconnect attempts from tab storage if not already set
    if (this.reconnectAttempts === 0 && params.chatId && params.tabId) {
      this.reconnectAttempts = getTabReconnectAttempts(params.chatId, params.tabId);
    }

    this.setStatus('connecting');

    const websocketUrl = `${params.baseWsUrl}/websocket`;
    if (!params.baseWsUrl || !params.chatId) {
      this.logger.error("WebSocket: Cannot connect, missing WS URL or Widget ID.");
      this.setStatus('error');
      this.emit('error', { message: "Cannot connect, missing configuration" });
      return Promise.reject(new Error("Missing configuration"));
    }

    const sessionIdToUse = params.sessionId || "init";
    const visitorSidParam = params.visitorSid
      ? `&visitorSid=${encodeURIComponent(params.visitorSid)}`
      : "";
    const tabIdParam = params.tabId
      ? `&tabId=${encodeURIComponent(params.tabId)}`
      : "";
    const urlWithParams =
      `${websocketUrl}?region=${params.region}&id=${params.chatId}` +
      `&accountId=${params.accountId}` +
      `&sessionId=${encodeURIComponent(sessionIdToUse)}` +
      `${visitorSidParam}${tabIdParam}`;

    // Create and store the connection promise
    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.webSocket = new WebSocket(urlWithParams);

        this.webSocket.onopen = () => {
          const wasReconnecting = this.reconnectAttempts > 0;
          this.reconnectAttempts = 0;
          
          if (params.chatId && params.tabId) {
            setTabReconnectAttempts(params.chatId, params.tabId, 0);
          }
          
          this.setStatus('connected');
          this.startHeartbeat();
          
          this.emit('open', { wasReconnecting });
          
          // Resolve the promise when connection opens
          this.connectionPromise = null;
          resolve();
        };

      this.webSocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // The initial connection may use the "init" placeholder. Retain the
          // server-assigned ID so an automatic reconnect returns to this DO
          // instead of creating a second session.
          if (
            message.type === 'sessionAssigned' &&
            message.sessionId &&
            (!params.sessionId || params.sessionId === 'init')
          ) {
            params = {
              ...params,
              sessionId: message.sessionId,
            };
          }

          if (message.type === 'pong') {
            this.handlePong(message);
            return;
          }
          
          this.emit('message', message);
        } catch (error) {
          this.logger.error("WebSocket: Error parsing message:", error);
          this.emit('error', { message: "Error parsing message", error });
        }
      };

        this.webSocket.onerror = (error) => {
          this.logger.error("WebSocket: Connection error:", error);
          this.emit('error', { message: "Connection error", error });
          
          // Reject the promise if we're still connecting
          if (this.connectionPromise) {
            this.connectionPromise = null;
            reject(new Error("WebSocket connection error"));
          }
        };

        this.webSocket.onclose = (event) => {
          this.logger.info(`WebSocket: Connection closed. Code: ${event.code}, Reason: ${event.reason}`);
          this.stopHeartbeat();
          this.webSocket = null;

          const wasClean = event.code === 1000 || event.code === 1001;
          this.emit('close', { code: event.code, reason: event.reason, wasClean });

          // Reject the promise if we're still connecting
          if (this.connectionPromise) {
            this.connectionPromise = null;
            reject(new Error(`WebSocket closed during connection: ${event.reason}`));
          }

          if (!wasClean && event.code !== 1008) {
            this.scheduleReconnect(params);
          } else {
            this.setStatus('closed');
          }
        };
      } catch (error) {
        this.logger.error("WebSocket: Failed to create connection:", error);
        this.emit('error', { message: "Failed to create connection", error });
        this.scheduleReconnect(params);
        this.connectionPromise = null;
        reject(error);
      }
    });
    
    return this.connectionPromise;
  }

  disconnect(code = 1000, reason = "Client disconnect") {
    this.stopHeartbeat();
    clearTimeout(this.reconnectTimeout);

    if (this.webSocket) {
      // Remove event handlers BEFORE closing to prevent the old onclose
      // from firing and stomping on new connection state
      this.webSocket.onopen = null;
      this.webSocket.onmessage = null;
      this.webSocket.onerror = null;
      this.webSocket.onclose = null;
      this.webSocket.close(code, reason);
      this.webSocket = null;
    }

    this.setStatus('disconnected');
  }

  send(data) {
    if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
      try {
        const message = typeof data === 'string' ? data : JSON.stringify(data);
        this.webSocket.send(message);
        return true;
      } catch (error) {
        this.logger.error("WebSocket: Error sending message:", error);
        this.emit('error', { message: "Error sending message", error });
        return false;
      }
    }
    return false;
  }

  // Reconnection logic
  scheduleReconnect(params) {
    if (this.connectionStatus === 'disconnected' || this.connectionStatus === 'ended') {
      return;
    }

    clearTimeout(this.reconnectTimeout);

    const tabAttempts = params.chatId && params.tabId ? 
      getTabReconnectAttempts(params.chatId, params.tabId) : 0;

    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.logger.error(`WebSocket: Max reconnect attempts (${this.config.maxReconnectAttempts}) reached.`);
      this.setStatus('error');
      this.emit('error', { message: "Max reconnect attempts reached" });
      return;
    }

    this.reconnectAttempts++;
    if (params.chatId && params.tabId) {
      setTabReconnectAttempts(params.chatId, params.tabId, this.reconnectAttempts);
    }

    // Exponential backoff with jitter
    const baseDelay = Math.min(
      this.config.reconnectDelayMs * Math.pow(2, this.reconnectAttempts - 1),
      30000 // Max delay 30 seconds
    );

    // Add ±20% jitter
    const jitterFactor = 0.2;
    const jitter = baseDelay * jitterFactor * (2 * Math.random() - 1);
    const delay = Math.round(baseDelay + jitter);

    // Additional delay for tabs that have been open longer
    const tabDelay = tabAttempts > 3 ? (tabAttempts - 3) * 1000 : 0;
    const totalDelay = delay + tabDelay;

    this.logger.debug(`WebSocket: Reconnecting in ${totalDelay}ms (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})...`);
    
    this.setStatus('reconnecting');
    this.emit('statusChange', { 
      status: 'reconnecting', 
      attempt: this.reconnectAttempts,
      maxAttempts: this.config.maxReconnectAttempts,
      delay: totalDelay
    });

    this.reconnectTimeout = setTimeout(() => {
      this.connect(params);
    }, totalDelay);
  }

  // Heartbeat mechanism
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing timers

    this.heartbeatInterval = setInterval(() => {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.sendPing();
      }
    }, this.HEARTBEAT_INTERVAL_MS);
  }

  pauseHeartbeat() {
    this.stopHeartbeat();
  }

  resumeHeartbeat() {
    if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
      return;
    }
    if (!this.heartbeatInterval) {
      this.startHeartbeat();
    }
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  sendPing() {
    if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.lastPingTime = Date.now();
    // This exact payload is handled by Durable Object WebSocket
    // auto-response without waking a hibernated object.
    this.send(HEARTBEAT_REQUEST);

    // Set timeout for pong response
    this.heartbeatTimeout = setTimeout(() => {
      this.logger.error('WebSocket: Pong timeout - no response to ping');
      // Force close the connection to trigger reconnect
      if (this.webSocket) {
        this.webSocket.close(1008, 'Ping timeout');
      }
    }, this.HEARTBEAT_TIMEOUT_MS);
  }

  handlePong(message) {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    if (this.lastPingTime) {
      const latency = Date.now() - this.lastPingTime;
      this.logger.debug(`WebSocket: Pong received (latency: ${latency}ms)`);
    }
  }

  // Status management
  setStatus(status) {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.emit('statusChange', { status });
    }
  }

  getStatus() {
    return this.connectionStatus;
  }

  isConnected() {
    return this.webSocket && this.webSocket.readyState === WebSocket.OPEN;
  }
}
